import { Group, type Opportunity } from "@entities";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { CircularProgress, Stack } from "@mui/material";
import { groupRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import {
  type QueryObserverResult,
  type RefetchOptions,
} from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { type EntityFilter } from "remult";
import GroupCard from "../../../components/popups/group-card/GroupCard";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useAddGroupToOpportunity } from "../../../queries/events.query";
import { ADMIN_BULK_ACTION_LIMIT } from "../../../helpers/admin-list-query";
import { useGroupColumns } from "./columns";
import filters from "./filters";
import "./GroupsTable.scss";
import "./VolunteersGroupsTable.scss";

const OptionalVolunteerGroupsTable: FC<{
  opportunity?: Opportunity;
  refetchEvent: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<Opportunity | null | undefined, Error>>;
  tableId?: string;
  previewRows?: Group[];
}> = ({
  opportunity,
  refetchEvent,
  tableId = "optional-volunteer-groups-table",
  previewRows,
}) => {
  const { HyperTable } = useCreateHyperTable(Group);
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const { mutateAsync: addGroupToOpportunity } = useAddGroupToOpportunity();
  const [isAddGroup, setIsAddGroup] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [group, setGroup] = useState<Group>();
  const buttons: ActionButton<Group>[] = useMemo(() => {
    return [
      {
        icon: <GroupAddIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate || !opportunity?.id) return;

          const groups = await groupRepo.find({
            where: whereToUpdate,
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });
          const toProcess = groups.slice(0, ADMIN_BULK_ACTION_LIMIT);

          if (toProcess.length === 0) return;

          try {
            await addGroupToOpportunity({
              opportunityId: opportunity.id,
              groupIds: toProcess.map((row) => row.id),
              defaultMatchWeight: opportunity.defaultMatchWeight,
            });
            if (groups.length > ADMIN_BULK_ACTION_LIMIT) {
              setAlertText(
                `נוספו עד ${ADMIN_BULK_ACTION_LIMIT} קבוצות. חזרו על הפעולה לשאר.`,
              );
              setSeverity("success");
            }
            refetchEvent();
            HyperTable.context.current?.refetch("remove");
          } catch {
            // useAddGroupToOpportunity onError handles alert
          }
        },
        title: "הוספה למשימה",
      },
    ];
  }, [opportunity, HyperTable, refetchEvent, addGroupToOpportunity, setAlertText, setSeverity]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId={tableId}
      findConfig={{
        enabled: true,
        where: (previewRows
          ? {}
          : {
              ...Group.hyperFilter({
                $and: [
                  {
                    opportunities: {
                      $none: {
                        opportunityId: opportunity?.id,
                      },
                    },
                  },
                  {
                    interestedOpportunities: {
                      $none: {
                        opportunity: { $is: { id: opportunity?.id } },
                      },
                    },
                  },
                ],
                organization: { $id: currOrganization?.id },
                isDeleted: false,
                volunteerOrganizationId: null,
              }),
              organization: { $id: currOrganization?.id },
            }) as EntityFilter<Group>,
        include: {
          admin: true,
          organization: true,
        },
      }}
      queryOptions={
        previewRows
          ? {
              dataFn: async () => previewRows,
              countFn: async () => previewRows.length,
              deps: [previewRows.map((row) => row.id).join(",")],
            }
          : undefined
      }
      onRowClick={
        previewRows
          ? undefined
          : (_, row) => {
              setIsAddGroup(true);
              setIsEdit(true);
              setGroup(row.original);
            }
      }
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {useGroupColumns(currOrganization, setAlertText, setSeverity).map(
        (column) => (
          <HyperTable.Column key={column.field} {...column} />
        ),
      )}
      <GroupCard
        isEdit={isEdit}
        isOpen={isAddGroup}
        onEditComplete={() => {
          HyperTable.context.current?.refetch("remove");
        }}
        onClose={() => {
          setIsAddGroup(false);
          setGroup(undefined);
        }}
        groupDetails={group}
      />
      <HyperTable.SelectColumn />
      <HyperTable.ActionsBar buttons={buttons} />
    </HyperTable>
  );
};

export default OptionalVolunteerGroupsTable;
